import { createSelector } from 'reselect';
import { StoreType } from '../../store/spirs/types';
import { Category, FixtureElement, Level, matchRouteParams, Sport, Tournament } from '../../models/api/codegen/code';

export const makeSelectSports = () => {
    return createSelector([(state: StoreType) => state.spirs.trees[state.type].s], data => {
        let sports = Object.values(data);

        // PABKSG-28059
        const firstItem = sports.filter(a => a.bo !== 0).sort((a: Sport, b: Sport) => a.bo - b.bo);

        const secondItem = sports.filter(a => a.bo === 0).sort(
            (a: Sport, b: Sport) => {
                let nameA = a.sn.toLowerCase(), nameB = b.sn.toLowerCase();
                if (nameA < nameB)
                    return -1;
                if (nameA > nameB)
                    return 1;
                return 0;
            }
        );

        return firstItem.concat(secondItem);
    });
};

export const makeSelectCategoryTypesBySport = () => {
    return createSelector(
        [(state: StoreType) => state.spirs.trees[state.type].c],
        categories => {
            const result: Record<number, Category[]> = {};

            for (const category of Object.values(categories)) {
                const sportId = category.si;
                (result[sportId] ??= []).push(category);
            }

            for (const sportId in result) {
                const items = result[sportId];

                const first = items.filter(c => c.bo !== 0)
                    .sort((a, b) => a.bo - b.bo);

                const second = items
                    .filter(c => c.bo === 0)
                    .sort((a, b) => a.cn.localeCompare(b.cn));

                result[sportId] = first.concat(second);
            }

            return result;
        }
    );
};

export const makeSelectTournamentsByCategoryType = () => {
    return createSelector(
        [(state: StoreType) => state.spirs.trees[state.type].t],
        tournaments => {
            const result: Record<number, Tournament[]> = {};

            for (const tournament of Object.values(tournaments)) {
                const categoryTypeId = tournament.ci;
                (result[categoryTypeId] ??= []).push(tournament);
            }

            for (const sportId in result) {
                const items = result[sportId];

                const first = items.filter(c => c.bo !== 0)
                    .sort((a, b) => a.bo - b.bo);

                const second = items
                    .filter(c => c.bo === 0)
                    .sort((a, b) => a.tn.localeCompare(b.tn));

                result[sportId] = first.concat(second);
            }

            return result;
        }
    );
};

export const makeSelectFixturesByTournament = () => {
    return createSelector(
        [(state: StoreType) => state.spirs.trees[state.type].fixtures],
        fixtures => {
            const result: Record<number, FixtureElement[]> = {};

            for (const fixture of Object.values(fixtures)) {
                const tournamentId = fixture.tid;
                (result[tournamentId] ??= []).push(fixture);
            }

            return result;
        }
    );
};

export const makeSelectTree = () => {
    return createSelector([(state: StoreType) => state.spirs.trees[state.type]], tree => tree);
};

export const makeSelectFilterTree = () => {
    return createSelector(
        [
            (state: StoreType) => state.spirs.trees[state.type],
            (_: StoreType, params: matchRouteParams) => params
        ],
        (tree, params) => {

            const findCategoryTypeId = (sportId: number, categoryId: number) => {
                const categories = tree.l.filter(level => {
                    return level.l === 'CATEGORY' && level.si === sportId && level.i === categoryId;
                });
                if (categories.length > 0) {
                    return categories[0].pi;
                }
                return null;
            }

            if (!params.sportId && !params.categoryId && !params.tournamentId) {
                return tree;
            }

            const levels = tree.l.filter(level => {
                switch (level.l) {
                    case 'SPORT':
                        return !params.sportId || level.i === Number(params.sportId);
                    case 'CATEGORY':
                        if (!params.sportId && !params.categoryId && !params.tournamentId) {
                            return true;
                        }
                        let bySport = true;
                        if (params.sportId) {
                            bySport = level.si === Number(params.sportId);
                        }
                        let byCategory = true;
                        if (params.categoryId) {
                            byCategory = level.pi === Number(params.categoryId);
                        }
                        let byTournament = true;
                        if (params.tournamentId) {
                            byTournament = level.i === Number(params.tournamentId);
                        }
                        return bySport && byCategory && byTournament;
                    case 'FIXTURE':
                        let sport = true;
                        if (params.sportId) {
                            sport = level.si === Number(params.sportId);
                        }
                        let category = true;
                        if (params.categoryId) {
                            const categoryId = findCategoryTypeId(level.si, level.pi);
                            category = !categoryId || categoryId === Number(params.categoryId);
                        }
                        return sport && category && (!params.tournamentId || level.pi === Number(params.tournamentId));
                    default:
                        return true;
                }
            });
            return {...tree, l: levels};
        });
};

export const makeSelectRouteTreeType = () => {
    return createSelector([(state: StoreType) => state.type], type => {
        switch (type) {
            case 'RATING_PREMATCH':
                return "/sport/rating/";
            case 'PREMATCH':
                return "/sport/prematch/";
            case 'LIVE':
                return "/sport/live/";
            default:
                return "";
        }
    });
};